import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  MenuItem,
  Select,
  TextField,
  Box,
  CircularProgress,
} from "@mui/material";
import SearchBar from "../../components/SearchBar";
import ViewChatModal from "../../Modals/addChat";
import { useGetCommunityGroups, useDeleteCommunityGroup, useUpdateCommunityGroup } from "../../api/hooks/useCommunity";
import { useGetCategories } from "../../api/hooks/useCategory";
import { showSuccess, showError } from "../../helpers/toast";
import type { CommunityGroup } from "../../api/types/community.types";
import type { Category } from "../../api/types/category.types";
import Pagination from "../../components/Panigation";
import ConfirmationDialog from "../../components/ConfirmationDialog";

const ManageCommunityGroups: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<string | null>(null);

  const [viewChatOpen, setViewChatOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<CommunityGroup | null>(null);

  // Debounce search term
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1); // Reset to page 1 when search changes
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  // Refetch groups when component mounts (to ensure fresh data after add/edit)
  useEffect(() => {
    refetchGroups();
  }, []); // Only run on mount

  // Fetch categories for filter dropdown
  const { data: categoriesData } = useGetCategories({}, { enabled: true });

  // Extract categories for filter
  const categories = useMemo(() => {
    const data = categoriesData?.data;
    let cats: Category[] = [];
    
    if (Array.isArray(data)) {
      cats = data;
    } else if (data && typeof data === 'object') {
      const dataObj = data as any;
      if ('list' in dataObj && Array.isArray(dataObj.list)) {
        cats = dataObj.list;
      } else if ('data' in dataObj && Array.isArray(dataObj.data)) {
        cats = dataObj.data;
      }
    }
    
    return cats.filter((cat) => cat.status && !cat.isDeleted);
  }, [categoriesData]);

  // Create category map for quick lookup
  const categoryMap = useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach((cat) => {
      map.set(cat._id, cat.name);
    });
    return map;
  }, [categories]);

  // Helper function to get category name (handles both string and object categoryId)
  const getCategoryName = (categoryId: any): string => {
    if (!categoryId) return "-";
    if (typeof categoryId === "string") {
      return categoryMap.get(categoryId) || "-";
    }
    if (typeof categoryId === "object" && categoryId.name) {
      return categoryId.name;
    }
    if (typeof categoryId === "object" && categoryId._id) {
      return categoryMap.get(categoryId._id) || categoryId.name || "-";
    }
    return "-";
  };

  // Fetch community groups
  const { data: groupsData, isLoading: isLoadingGroups, refetch: refetchGroups, error: groupsError } = useGetCommunityGroups(
    {
      ...(debouncedSearchTerm.trim() && { search: debouncedSearchTerm.trim() }),
      page,
      limit,
      offset: (page - 1) * limit,
    },
    { 
      enabled: true,
      refetchOnMount: true, // Always refetch when component mounts
      refetchOnWindowFocus: false, // Don't refetch on window focus
    }
  );

  // Extract groups and totalCount from response
  const { groups, totalCount } = useMemo(() => {
    const data = groupsData?.data;
    let groupsList: CommunityGroup[] = [];
    let total = 0;
    
    if (Array.isArray(data)) {
      groupsList = data;
      total = data.length;
    } else if (data && typeof data === 'object') {
      const dataObj = data as any;
      if ('list' in dataObj && Array.isArray(dataObj.list)) {
        groupsList = dataObj.list;
        total = dataObj.totalCount || dataObj.list.length;
      } else if ('data' in dataObj && Array.isArray(dataObj.data)) {
        groupsList = dataObj.data;
        total = dataObj.totalCount || dataObj.data.length;
      } else if (dataObj.totalCount !== undefined) {
        total = dataObj.totalCount;
      }
    }
    
    if (!Array.isArray(groupsList)) {
      groupsList = [];
    }
    
    return {
      groups: groupsList,
      totalCount: total || groupsList.length,
    };
  }, [groupsData]);

  // Helper function to get category ID (handles both string and object)
  const getCategoryIdValue = (categoryId: any): string => {
    if (!categoryId) return "";
    if (typeof categoryId === "string") return categoryId;
    if (typeof categoryId === "object" && categoryId._id) return categoryId._id;
    return "";
  };

  // Filter groups by category and date (client-side filtering for date)
  const filteredGroups = useMemo(() => {
    return groups.filter((g) => {
      const categoryIdValue = getCategoryIdValue(g.categoryId);
      const matchCategory = filterCategory ? categoryIdValue === filterCategory : true;
      const matchDate = filterDate ? g.createdAt?.startsWith(filterDate) : true;
      return matchCategory && matchDate;
    });
  }, [groups, filterCategory, filterDate]);

  // Format date helper
  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) return "-";
      // Format as DD/MM/YYYY
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return "-";
    }
  };

  // Handle status toggle
  const { mutate: updateGroupStatus, isPending: isUpdatingStatus } = useUpdateCommunityGroup({
    onSuccess: (response) => {
      showSuccess(response.message || "Status updated successfully");
      refetchGroups();
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to update status");
    },
  });

  const handleToggleStatus = (group: CommunityGroup) => {
    updateGroupStatus({
      id: group._id,
      data: { status: !group.status },
    });
  };

  // Handle delete
  const { mutate: deleteGroup, isPending: isDeleting } = useDeleteCommunityGroup({
    onSuccess: (response) => {
      showSuccess(response.message || "Group deleted successfully");
      refetchGroups();
      setDeleteDialogOpen(false);
      setGroupToDelete(null);
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to delete group");
    },
  });

  const handleDelete = (id: string) => {
    setGroupToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (groupToDelete) {
      deleteGroup(groupToDelete);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setGroupToDelete(null);
  };

  return (
    <>
      <div className="main_title">
        <h1>Manage Community Groups</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          Manage Community Groups
        </p>
      </div>

      <div className="cards">
        {/* Header */}
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setSearchTerm}
              placeholder="Search Groups"
              value={searchTerm}
              onChange={(e:any) => setSearchTerm(e.target.value)}
              onCross={() => {
                setSearchTerm("");
                setDebouncedSearchTerm("");
              }}
            />
          </div>

          <div className="right_s" style={{ display: "flex", gap: "10px" }}>
            {/* Filter by Category */}
            <div className="form">
              <div className="control_group">
                <Select
                  value={filterCategory}
                  onChange={(e) => {
                    setFilterCategory(e.target.value);
                    setPage(1);
                  }}
                  displayEmpty
                  className="filter_select"
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat._id} value={cat._id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </div>

            {/* Filter by Date */}
              <div className="control_group w_33">
                <TextField
                  type="date"
                  fullWidth
                  value={filterDate}
                  onChange={(e) => {
                    setFilterDate(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
            </div>

            <Button className="btn btn_primary" onClick={() => navigate("add")}>
              + Add Group
            </Button>
          </div>
        </div>

        {/* Table */}
        <TableContainer className="table_container">
          {isLoadingGroups ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <CircularProgress />
            </Box>
          ) : groupsError ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <p>Error loading community groups. Please try again.</p>
              <Button onClick={() => refetchGroups()} style={{ marginLeft: 10 }}>
                Retry
              </Button>
            </Box>
          ) : filteredGroups.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <p>
                {debouncedSearchTerm.trim() || filterCategory || filterDate
                  ? "No groups found matching your filters"
                  : "No community groups found"}
              </p>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Group Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Members</TableCell>
                  <TableCell>Created Date</TableCell>
                  <TableCell>Group Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredGroups.map((group) => (
                  <TableRow
                    key={group._id}
                    style={{ opacity: group.status ? 1 : 0.6 }}
                  >
                    <TableCell>{group.name}</TableCell>
                    <TableCell>{getCategoryName(group.categoryId)}</TableCell>
                    <TableCell>{group.memberCount}</TableCell>
                    <TableCell>{formatDate(group.createdAt || (group as any).createdDate)}</TableCell>

                    {/* Active / Deactive */}
                    <TableCell>
                      <Switch
                        checked={group.status ?? true}
                        onChange={() => handleToggleStatus(group)}
                        disabled={isUpdatingStatus}
                      />
                    </TableCell>

                    {/* Actions */}
                    <TableCell>
                      <div className="table_actions v2">
                        <Button
                          className="btn btn_primary sm"
                          onClick={() => {
                            setSelectedGroup(group);
                            setViewChatOpen(true);
                          }}
                        >
                          View Chat
                        </Button>

                        <IconButton
                          onClick={() => navigate("view", { state: { group } })}
                        >
                          <img
                            src="/static/images/eye_icon.svg"
                            alt="View"
                          />
                        </IconButton>

                        <IconButton
                          onClick={() => navigate("edit", { state: { group } })}
                        >
                          <img src="/static/images/edit2_icon.svg" alt="Edit" />
                        </IconButton>

                        <IconButton
                          onClick={() => handleDelete(group._id)}
                          disabled={isDeleting}
                        >
                          <img
                            src="/static/images/trash2_icon.svg"
                            alt="Delete"
                          />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {/* Pagination */}
        {!isLoadingGroups && !groupsError && totalCount > 0 && (
          <Pagination
            module={filteredGroups}
            page={page}
            onPageChange={(newPage) => {
              setPage(newPage);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            totalPages={Math.ceil(totalCount / limit)}
            totalCount={totalCount}
            setPage={setPage}
            limit={limit}
          />
        )}
      </div>

      {/* Chat Modal */}
      <ViewChatModal
        open={viewChatOpen}
        setOpen={setViewChatOpen}
        onClose={() => setViewChatOpen(false)}
        selectedGroup={selectedGroup}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Community Group"
        message="Are you sure you want to delete this community group? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonColor="error"
        isLoading={isDeleting}
        loadingText="Deleting..."
      />
    </>
  );
};

export default ManageCommunityGroups;
